// biome-ignore-all lint/a11y/useSemanticElements: <input type="radio">
//   cannot be styled as a tier-pill (see Seg primitive justification —
//   composite-widget radios on non-input elements are explicitly allowed
//   by W3C ARIA APG: https://www.w3.org/WAI/ARIA/apg/patterns/radio/).
//
// Phase 15 Plan 06 Task 3 — Bot tier picker modal.
//
// role="dialog" + aria-modal bottom-sheet for the Bot create-match flow.
// User picks a bot tier (easy / medium / hard) + an optional "fast bot"
// toggle, then submits → POST /api/match/create with `{mode:'bot', ...}`
// + a fresh Idempotency-Key. On 200/201 → navigate to /match/{matchId}.
//
// Schema note (deviation Rule 1 — adapt to real schema):
//   MatchCreateRequest in the OpenAPI schema has FLAT fields (no nested
//   `config: { botTier, fastBot }`). The plan's "config:{...}" wording
//   is a simplification — the actual schema expects:
//     { mode: 'bot', language: 'en' | 'sv' }
//   Bot tier / fast-bot are recorded in component state for future
//   wiring once the backend accepts them. The match-create call here
//   ships the minimum-valid payload so the modal advances the user to
//   /match/{id} (where Phase 14's match screen takes over).

import { useStore } from '@nanostores/react'
import { type JSX, useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { ensureAnonSession } from '../../auth/anon'
import { apiFetch } from '../../net/client'
import { createIdempotencyKey } from '../../net/idempotency'
import { useRouter } from '../../router/router'
import { $locale } from '../../stores/locale'
import { Btn } from '../components/Btn'
import { Seg } from '../components/Seg'
import { Sheet } from '../components/Sheet'

type BotTier = 'easy' | 'medium' | 'hard'

// ARCA-130 — the fast-bot control is a two-option segmented toggle (design
// system parity), not a raw native checkbox. 'fast' ⇄ the boolean fastBot.
type FastBotMode = 'off' | 'fast'

export interface BotTierModalProps {
  readonly open: boolean
  readonly onClose: () => void
}

interface MatchCreateResponse {
  readonly matchId: string
  readonly websocketUrl?: string
}

const TIERS: ReadonlyArray<{
  value: BotTier
  messageId: string
  defaultLabel: string
}> = [
  {
    value: 'easy',
    messageId: 'social.lobby.bot_tier.easy',
    defaultLabel: 'Easy',
  },
  {
    value: 'medium',
    messageId: 'social.lobby.bot_tier.medium',
    defaultLabel: 'Medium',
  },
  {
    value: 'hard',
    messageId: 'social.lobby.bot_tier.hard',
    defaultLabel: 'Hard',
  },
]

export function BotTierModal({
  open,
  onClose,
}: BotTierModalProps): JSX.Element | null {
  const router = useRouter()
  const intl = useIntl()
  const locale = useStore($locale)
  const [selectedTier, setSelectedTier] = useState<BotTier>('medium')
  const [fastBot, setFastBot] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      // ARCA-93 / WS2: mint the anon practice session HERE — on entering
      // practice — for a guest with no session yet. No-op for an authenticated
      // user (their real bearer is used) or if an anon session already exists.
      // This is what lets the splash "Practice vs the bot" entry create a bot
      // match with zero sign-in.
      await ensureAnonSession()
      const result = await apiFetch<MatchCreateResponse>(
        '/api/match/create',
        'POST',
        {
          body: { mode: 'bot', language: locale },
          idempotencyKey: createIdempotencyKey(),
        },
      )
      // Persist tier + fastBot in state so future backend support reads them
      // (no-op for now; the modal advances the user regardless).
      void selectedTier
      void fastBot
      onClose()
      router.navigate(`/match/${result.matchId}`)
    } catch {
      setError(
        intl.formatMessage({
          id: 'social.error.bot_create_failed',
          defaultMessage: "Couldn't start a bot match. Try again.",
        }),
      )
    } finally {
      setSubmitting(false)
    }
  }, [submitting, selectedTier, fastBot, onClose, router, intl, locale])

  if (!open) return null

  return (
    // Bottom-sheet flavor (UI-SPEC §5): scrim + grab handle + focus trap +
    // scrim-tap/Escape dismissal all live in <Sheet>/ModalScrim.
    // dismissOnScrimTap={!submitting} guards against accidental close during submit.
    <Sheet
      onClose={onClose}
      testId="bot-tier-modal"
      labelledBy="bot-tier-modal-title"
      dismissOnScrimTap={!submitting}
    >
      {/* Header */}
      <div
        style={{
          height: '48px',
          backgroundColor: 'var(--color-surface-deep)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          borderBottom: '3px solid var(--color-surface-stroke)',
        }}
      >
        <h2
          id="bot-tier-modal-title"
          style={{
            margin: 0,
            fontFamily: 'Fredoka, sans-serif',
            fontWeight: 700,
            fontSize: 'var(--font-size-18)',
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="social.lobby.bot_tier_modal.title"
            defaultMessage="Choose bot difficulty"
          />
        </h2>
      </div>
      {/* Body */}
      <div
        style={{
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
        }}
      >
        {/* Tier pills */}
        <div
          role="radiogroup"
          aria-label={intl.formatMessage({
            id: 'social.lobby.bot_tier_modal.tier_group_aria',
            defaultMessage: 'Bot difficulty',
          })}
          style={{ display: 'flex', gap: 'var(--space-2)' }}
        >
          {TIERS.map((tier) => {
            const isSelected = tier.value === selectedTier
            return (
              <button
                key={tier.value}
                type="button"
                role="radio"
                aria-checked={isSelected ? 'true' : 'false'}
                data-tier={tier.value}
                onClick={() => setSelectedTier(tier.value)}
                style={{
                  flex: 1,
                  height: '48px',
                  backgroundColor: isSelected
                    ? 'var(--color-cta-primary)'
                    : 'var(--color-surface-deep)',
                  color: isSelected
                    ? 'var(--color-inkwell)'
                    : 'var(--color-text-primary)',
                  border: '2px solid var(--color-surface-stroke)',
                  borderRadius: 'var(--radius-lg)',
                  fontFamily: 'Fredoka, sans-serif',
                  fontWeight: 700,
                  fontSize: 'var(--font-size-14)',
                  cursor: 'pointer',
                }}
              >
                <FormattedMessage
                  id={tier.messageId}
                  defaultMessage={tier.defaultLabel}
                />
              </button>
            )
          })}
        </div>
        {/* Fast-bot toggle — styled Off/Fast segmented control (ARCA-130;
            mirrors the Settings <Seg> boolean-toggle pattern). The visible
            label stays on the left; the Seg radiogroup is aria-labelled by the
            same string. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
          }}
        >
          <span
            style={{
              color: 'var(--color-text-primary)',
              fontFamily: 'Manrope, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 'var(--font-size-14)',
            }}
          >
            <FormattedMessage
              id="social.lobby.bot_tier_modal.fast_bot_label"
              defaultMessage="Fast bot (responds immediately)"
            />
          </span>
          <Seg<FastBotMode>
            testId="bot-fast-toggle"
            ariaLabel={intl.formatMessage({
              id: 'social.lobby.bot_tier_modal.fast_bot_label',
              defaultMessage: 'Fast bot (responds immediately)',
            })}
            value={fastBot ? 'fast' : 'off'}
            onChange={(next) => setFastBot(next === 'fast')}
            options={[
              {
                value: 'off',
                label: (
                  <FormattedMessage
                    id="social.lobby.bot_tier_modal.fast_bot.off"
                    defaultMessage="Off"
                  />
                ),
              },
              {
                value: 'fast',
                label: (
                  <FormattedMessage
                    id="social.lobby.bot_tier_modal.fast_bot.fast"
                    defaultMessage="Fast"
                  />
                ),
              },
            ]}
          />
        </div>
        {error !== null && (
          <p
            role="alert"
            style={{
              margin: 0,
              color: 'var(--color-cta-destructive)',
              fontFamily: 'Manrope, system-ui, sans-serif',
              fontSize: 'var(--font-size-13)',
            }}
          >
            {error}
          </p>
        )}
        {/* Primary CTA */}
        <Btn
          kind="primary"
          size="lg"
          block
          testId="bot-tier-submit"
          disabled={submitting}
          onClick={() => {
            void handleSubmit()
          }}
        >
          <FormattedMessage
            id="social.lobby.bot_tier_modal.start_cta"
            defaultMessage="Start practice"
          />
        </Btn>
      </div>
    </Sheet>
  )
}
